/**
 * Whether Grow is updating its advice in the background while the old advice
 * is on screen (owner, 2026-10-16: "when things are updating or there needs to
 * be an update in the background, that's fine. But we need to communicate that
 * to the user").
 *
 * True when the Today brief is being read again for a change (a bed or plant
 * saved, the garden's midnight: lib/briefFreshness; not a read on coming back
 * to the page), with an earlier one showing, or when the server says it is
 * rebuilding the garden's outlook as it answers (the brief's `updating`: the
 * server decides, the app displays). It does not read the plant outlook
 * itself (W24 Job C): the brief says when the outlook is being rebuilt, and
 * reading it here kept GET /plants/outlook open on Today, so every to-do mark
 * downloaded it again. Never true for a first load with nothing to show: that is the
 * screen's own loader. A change shows only after SHOW_AFTER_MS, so a read that
 * is over in a blink flashes nothing; it goes away at once.
 *
 * Where: GuidanceUpdating and GuidanceQuiet (Today, the top of Plants, Beds,
 * Weather).
 * API: none of its own; it reads useTodayBrief.
 *
 * @module useGuidanceUpdating
 */

import { useEffect, useState } from 'react';
import { useTodayBrief } from './useTodayBrief';

/** How long an update must last before it is shown, in milliseconds. A read that ends sooner shows nothing. */
export const SHOW_AFTER_MS = 600;

/** What a page is told about the update. */
export interface GuidanceUpdatingReading {
  /** True while an update is on its way, has lasted SHOW_AFTER_MS and old content is showing. */
  updating: boolean;
  /** The failure of an update that failed with old content still showing, or null. */
  failure: unknown;
}

/**
 * Whether the advice is being updated, and the failure of an update that did not come.
 *
 * @returns The update's state.
 */
export function useGuidanceUpdating(): GuidanceUpdatingReading {
  // The brief read again for a change (not on coming back to the page: the same fingerprint changes nothing there).
  const { brief, isFetching: briefFetching, rereading, isError: briefFailed, error: briefError, serverUpdating, noZip } = useTodayBrief();
  // Old content to be quiet over: a brief held, read again for a change, or the server rebuilding the outlook as it answers.
  const raw = !noZip && brief !== undefined && (rereading || serverUpdating);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!raw) {
      setShown(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShown(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [raw]);
  return { updating: raw && shown, failure: briefFailed && brief !== undefined && !briefFetching ? briefError : null };
}
